import React from 'react';
import { Link } from 'react-router-dom';
import { Header } from '@/app/components/Header';
import { Footer } from '@/app/components/Footer';

export function WhyZaptopiaPage() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50 relative overflow-hidden">
      {/* Modern Background Design */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Subtle gradient mesh */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_120%,rgba(120,119,198,0.3),rgba(255,255,255,0))]"></div>
        
        {/* Animated gradient lines */}
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-0 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-indigo-500/50 to-transparent animate-gradient-line"></div>
          <div className="absolute top-1/4 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-purple-500/50 to-transparent animate-gradient-line" style={{ animationDelay: '1s' }}></div>
          <div className="absolute top-2/4 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-pink-500/50 to-transparent animate-gradient-line" style={{ animationDelay: '2s' }}></div>
          <div className="absolute top-3/4 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-blue-500/50 to-transparent animate-gradient-line" style={{ animationDelay: '3s' }}></div>
        </div>

        {/* Floating particles */}
        <div className="absolute inset-0">
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              className="absolute w-1 h-1 bg-indigo-500/20 rounded-full animate-float"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 5}s`,
                animationDuration: `${5 + Math.random() * 10}s`
              }}
            />
          ))}
        </div>

        {/* Subtle noise texture */}
        <div className="absolute inset-0 opacity-[0.015] mix-blend-overlay" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
        }}></div>
      </div>

      <Header />
      
      <main className="relative z-10">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-16">
          <div className="max-w-6xl mx-auto">
            {/* Enhanced Hero Section - Mobile Optimized */}
            <div className="text-center mb-12 sm:mb-24">
              <div className="relative inline-block">
                <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-black tracking-tight mb-3 sm:mb-4 leading-tight">
                  <span className="block text-gray-900">Find or Create</span>
                  <span className="block bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-transparent animate-gradient">
                    Any App
                  </span>
                </h1>
              </div>
              <p className="text-lg sm:text-xl lg:text-2xl text-gray-600 font-light leading-relaxed max-w-4xl mx-auto px-2 sm:px-4 mt-4 sm:mt-6">
                The place to find or request the app you need
              </p>
            </div>

            {/* Enhanced How It Works Section - Mobile Optimized */}
            <div className="mb-12 sm:mb-24">
              <div className="relative group">
                <div className="relative bg-white/90 backdrop-blur-xl rounded-3xl p-5 sm:p-6 lg:p-12 shadow-xl border border-gray-100">
                  <div className="flex flex-col sm:flex-row items-center mb-6 sm:mb-12">
                    <div className="relative">
                      <div className="relative w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-3xl flex items-center justify-center mb-4 sm:mb-0 sm:mr-6 shadow-lg">
                        <svg className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                      </div>
                    </div>
                    <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 text-center sm:text-left">
                      How It Works
                    </h2>
                  </div>
                  
                  <div className="space-y-6 sm:space-y-12">
                    <div className="flex flex-col sm:flex-row items-start group">
                      <div className="relative">
                        <div className="relative w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-r from-indigo-500 to-blue-500 text-white rounded-2xl flex items-center justify-center font-bold text-lg sm:text-xl mb-3 sm:mb-0 sm:mr-6 mt-2 flex-shrink-0 shadow-lg">
                          1
                        </div>
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 mb-2 sm:mb-4 group-hover:text-indigo-600 transition-colors duration-300">Search for Existing Solutions</h3>
                        <p className="text-base sm:text-lg lg:text-xl text-gray-700 leading-relaxed">
                          Describe what you need and our AI searches the web and app stores for existing solutions. 
                          Get instant results with quality assessments and direct links.
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex flex-col sm:flex-row items-start group">
                      <div className="relative">
                        <div className="relative w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-2xl flex items-center justify-center font-bold text-lg sm:text-xl mb-3 sm:mb-0 sm:mr-6 mt-2 flex-shrink-0 shadow-lg">
                          2
                        </div>
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 mb-2 sm:mb-4 group-hover:text-purple-600 transition-colors duration-300">Request What's Missing</h3>
                        <p className="text-base sm:text-lg lg:text-xl text-gray-700 leading-relaxed">
                          If nothing good exists, easily submit it as a request. 
                          The community can vote on your idea and show builders there's real demand.
                        </p>
                      </div>
                    </div>

                    <div className="flex flex-col sm:flex-row items-start group">
                      <div className="relative">
                        <div className="relative w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-r from-emerald-500 to-green-500 text-white rounded-2xl flex items-center justify-center font-bold text-lg sm:text-xl mb-3 sm:mb-0 sm:mr-6 mt-2 flex-shrink-0 shadow-lg">
                          3
                        </div>
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 mb-2 sm:mb-4 group-hover:text-emerald-600 transition-colors duration-300">Watch Ideas Come to Life</h3>
                        <p className="text-base sm:text-lg lg:text-xl text-gray-700 leading-relaxed">
                          Popular requests get built by developers who see proven market interest. 
                          You'll be notified when your requested app becomes reality.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Enhanced Why This Approach Works Section - Mobile Optimized */}
            <div className="mb-12 sm:mb-24">
              <div className="relative group">
                <div className="relative bg-white/90 backdrop-blur-xl rounded-3xl p-5 sm:p-6 lg:p-12 shadow-xl border border-gray-100">
                  <div className="flex flex-col sm:flex-row items-center mb-6 sm:mb-12">
                    <div className="relative">
                      <div className="relative w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 bg-gradient-to-r from-emerald-500 to-blue-500 rounded-3xl flex items-center justify-center mb-4 sm:mb-0 sm:mr-6 shadow-lg">
                        <svg className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                    </div>
                    <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 text-center sm:text-left">
                      Why This Approach Works
                    </h2>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
                    <div className="group relative">
                      <div className="relative bg-gradient-to-br from-indigo-50 to-blue-50 rounded-3xl p-5 sm:p-6 lg:p-8 border-2 border-indigo-100 hover:border-indigo-200 transition-colors shadow-lg">
                        <div className="relative">
                          <div className="relative w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 bg-gradient-to-r from-indigo-500 to-blue-500 rounded-2xl flex items-center justify-center mb-4 sm:mb-6 shadow-lg">
                            <svg className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                          </div>
                        </div>
                        <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2 sm:mb-4 group-hover:text-indigo-600 transition-colors duration-300">Immediate Value</h3>
                        <p className="text-base sm:text-lg text-gray-700">Get instant results when searching for apps, even if you end up not finding anything</p>
                      </div>
                    </div>

                    <div className="group relative">
                      <div className="relative bg-gradient-to-br from-purple-50 to-pink-50 rounded-3xl p-5 sm:p-6 lg:p-8 border-2 border-purple-100 hover:border-purple-200 transition-colors shadow-lg">
                        <div className="relative">
                          <div className="relative w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 bg-gradient-to-r from-purple-500 to-pink-500 rounded-2xl flex items-center justify-center mb-4 sm:mb-6 shadow-lg">
                            <svg className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                            </svg>
                          </div>
                        </div>
                        <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2 sm:mb-4 group-hover:text-purple-600 transition-colors duration-300">Natural Habit Formation</h3>
                        <p className="text-base sm:text-lg text-gray-700">Every time you think "I wish there was an app for X" → you come here</p>
                      </div>
                    </div>

                    <div className="group relative">
                      <div className="relative bg-gradient-to-br from-emerald-50 to-green-50 rounded-3xl p-5 sm:p-6 lg:p-8 border-2 border-emerald-100 hover:border-emerald-200 transition-colors shadow-lg">
                        <div className="relative">
                          <div className="relative w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 bg-gradient-to-r from-emerald-500 to-green-500 rounded-2xl flex items-center justify-center mb-4 sm:mb-6 shadow-lg">
                            <svg className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                            </svg>
                          </div>
                        </div>
                        <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2 sm:mb-4 group-hover:text-emerald-600 transition-colors duration-300">Proven Market Interest</h3>
                        <p className="text-base sm:text-lg text-gray-700">Builders can see real demand before investing time and effort</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Enhanced Value Proposition Section - Mobile Optimized */}
            <div className="mb-12 sm:mb-24">
              <div className="relative group">
                <div className="relative bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 rounded-3xl p-5 sm:p-6 lg:p-12 shadow-xl text-white">
                  <div className="flex flex-col sm:flex-row items-center mb-6 sm:mb-12">
                    <div className="relative">
                      <div className="relative w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 bg-white/20 backdrop-blur-sm rounded-3xl flex items-center justify-center mb-4 sm:mb-0 sm:mr-6 shadow-lg">
                        <svg className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                        </svg>
                      </div>
                    </div>
                    <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-center sm:text-left">Stop Wondering, Start Finding</h2>
                  </div>
                  
                  <div className="space-y-4 sm:space-y-6">
                    <p className="text-lg sm:text-xl lg:text-2xl leading-relaxed">
                      Every time you think "I wish there was an app for..." this is where you come.
                    </p>
                    
                    <div className="relative group">
                      <div className="relative bg-white/20 backdrop-blur-sm rounded-3xl p-5 sm:p-6 lg:p-8 border border-white/30 shadow-lg">
                        <h3 className="text-lg sm:text-xl lg:text-2xl font-bold mb-3 sm:mb-4">The new habit:</h3>
                        <div className="space-y-2 sm:space-y-3">
                          <p className="text-base sm:text-lg lg:text-xl flex items-center">
                            <span className="mr-2 sm:mr-3 text-xl sm:text-2xl">📱</span>
                            "I need an app for expense splitting" → Search here first
                          </p>
                          <p className="text-base sm:text-lg lg:text-xl flex items-center">
                            <span className="mr-2 sm:mr-3 text-xl sm:text-2xl">🔍</span>
                            Get instant results with quality assessments
                          </p>
                          <p className="text-base sm:text-lg lg:text-xl flex items-center">
                            <span className="mr-2 sm:mr-3 text-xl sm:text-2xl">🚀</span>
                            If nothing good exists → Request it and see who else wants it
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Enhanced Call to Action - Mobile Optimized */}
            <div className="text-center">
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-6 items-center justify-center">
                <Link
                  to="/"
                  className="w-full sm:w-auto group relative px-6 sm:px-8 lg:px-12 py-4 sm:py-5 lg:py-6 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-white font-bold text-base sm:text-lg lg:text-xl rounded-2xl shadow-lg hover:shadow-xl transition-all duration-500 transform hover:scale-105 hover:-translate-y-1"
                >
                  <div className="relative flex items-center justify-center">
                    <svg className="w-5 h-5 sm:w-6 sm:h-6 mr-2 sm:mr-3 transition-transform group-hover:rotate-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    Try App Discovery
                  </div>
                </Link>
                
                <Link
                  to="/requests"
                  className="w-full sm:w-auto group relative px-6 sm:px-8 lg:px-12 py-4 sm:py-5 lg:py-6 bg-white/90 backdrop-blur-sm text-gray-700 font-bold text-base sm:text-lg lg:text-xl rounded-2xl shadow-lg hover:shadow-xl transition-all duration-500 transform hover:scale-105 hover:-translate-y-1 border-2 border-gray-200 hover:border-indigo-300"
                >
                  <div className="relative flex items-center justify-center">
                    Browse Requests
                    <svg className="w-5 h-5 sm:w-6 sm:h-6 ml-2 sm:ml-3 transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </div>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />

      <style jsx>{`
        .animate-gradient {
          background-size: 200% 200%;
          animation: gradient 3s ease infinite;
        }
        
        @keyframes gradient {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }

        .animate-gradient-line {
          animation: gradient-line 8s linear infinite;
        }

        @keyframes gradient-line {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }

        .animate-float {
          animation: float linear infinite;
        }

        @keyframes float {
          0% { transform: translateY(0) translateX(0); }
          25% { transform: translateY(-20px) translateX(10px); }
          50% { transform: translateY(-40px) translateX(0); }
          75% { transform: translateY(-20px) translateX(-10px); }
          100% { transform: translateY(0) translateX(0); }
        }
      `}</style>
    </div>
  );
}